import { BitkitBadgeProps } from '@bitrise/bitkit-v2';
import { format } from 'date-fns';

import { Trend, trendProps, TypeTimeInterval } from '@/pages/Insights/Insights.types';
import { formatNumber } from '@/utils/commonFormats';

import { ThresholdUnit } from './pages/InsightsAlerts/InsightsAlerts.types';

export const getTrendDirection = (trend?: number) => {
  if (!trend || trend === 0) {
    return 'stagnant';
  }
  if (trend < 0) {
    return 'down';
  }
  return 'up';
};

export const getTrendProps = (trend?: number, isColored?: boolean, isReversedColor?: boolean) => {
  const direction = getTrendDirection(trend);
  let iconColor: Trend['iconColor'] = 'icon/secondary';
  if (isColored) {
    iconColor = trendProps[direction].iconColor;
    if (isReversedColor) {
      if (direction === 'down') {
        iconColor = trendProps.up.iconColor;
      } else {
        iconColor = trendProps.down.iconColor;
      }
    }
  }

  return {
    icon: trendProps[direction].icon,
    iconColor,
  };
};

export const getTrendColorVariant = (trend?: number, isColored?: boolean, isReversedColor?: boolean) => {
  let colorVariant: BitkitBadgeProps['colorVariant'] = 'neutral';
  if (isColored) {
    let direction: 'down' | 'up' | 'stagnant' = getTrendDirection(trend);
    if (isReversedColor && direction !== 'stagnant') {
      direction = direction === 'down' ? 'up' : 'down';
    }
    colorVariant = trendProps[direction].colorVariant;
  }

  return {
    colorVariant,
  };
};

export const metricTooltipDateText: Record<
  TypeTimeInterval,
  (dateFrom: Date | number, dateTo: Date | number) => { primaryDateText: string }
> = {
  secondly: (dateFrom: Date | number) => {
    return {
      primaryDateText: `${format(dateFrom, 'LLL dd hh:mm:ss a')}`,
    };
  },
  hourly: (dateFrom: Date | number, dateTo: Date | number) => {
    return {
      primaryDateText: `${format(dateFrom, 'LLL dd hh:mma')} - ${format(dateTo, 'hh:mma zzz')}`,
    };
  },
  daily: (dateFrom: Date | number, dateTo: Date | number) => {
    return {
      primaryDateText: `${format(dateFrom, 'LLL dd hh:mma')} - ${format(dateTo, 'LLL dd hh:mma zzz')}`,
    };
  },
  weekly: (dateFrom: Date | number, dateTo: Date | number) => {
    return {
      primaryDateText: `${format(dateFrom, 'LLL dd')} - ${format(dateTo, 'LLL dd zzz')}`,
    };
  },
  monthly: (dateFrom: Date | number, dateTo: Date | number) => {
    return {
      primaryDateText: `${format(dateFrom, 'LLL dd')} - ${format(dateTo, 'LLL dd zzz')}`,
    };
  },
};

export const breakdownTableHeaderDateText: Record<
  TypeTimeInterval,
  (dateFrom: Date | number, dateTo: Date | number) => string
> = {
  secondly: (dateFrom: Date | number) => {
    return `${format(dateFrom, 'LLL dd, h:mm:ss a')}`;
  },
  hourly: (dateFrom: Date | number) => {
    return `${format(dateFrom, 'LLL dd, ha')}`;
  },
  daily: (dateFrom: Date | number) => {
    return `${format(dateFrom, 'LLL dd')}`;
  },
  weekly: (dateFrom: Date | number, dateTo: Date | number) => {
    return `${format(dateFrom, 'LLL dd')} - ${format(dateTo, 'LLL dd')}`;
  },
  monthly: (dateFrom: Date | number, dateTo: Date | number) => {
    return `${format(dateFrom, 'LLL dd')} - ${format(dateTo, 'LLL dd')}`;
  },
};

export const convertThreshold = (value: number, unit: ThresholdUnit): number | undefined => {
  switch (unit) {
    case 'ms':
      return value / 1000;
    case 'min':
      return value * 60;
    case 'hrs':
      return value * 60 * 60;
    case 'days':
      return value * 60 * 60 * 24;
    case 'weeks':
      return value * 60 * 60 * 24 * 7;
    case 'kb':
      return value * 1024;
    case 'mb':
      return value * 1024 * 1024;
    case 'gb':
      return value * 1024 * 1024 * 1024;
    case 'tb':
      return value * 1024 * 1024 * 1024 * 1024;
    default:
      return value;
  }
};

export const secondsTimeResolution = (value: number) => {
  const duration = Math.floor(value);
  let time = duration;
  const seconds = Math.round(time % 60);
  time -= seconds;
  time /= 60;
  const allMinutes = time;
  const minutes = Math.round(time % 60);
  time -= minutes;
  const hours = time / 60;

  return {
    allMinutes,
    hours,
    minutes,
    seconds,
  };
};

export const secondsToHours = (value: number): string => {
  const parts = [];
  const { hours, minutes, seconds } = secondsTimeResolution(value);

  if (hours > 0) {
    parts.push(`${formatNumber(hours)}h`);
  }
  if (minutes > 0) {
    parts.push(`${minutes}m`);
  }
  if (seconds > 0) {
    parts.push(`${seconds}s`);
  }

  return parts.join(' ') || '0';
};

export const secondsToMinutes = (value: number): string => {
  const parts = [];
  const { allMinutes, seconds } = secondsTimeResolution(value);

  if (allMinutes > 0) {
    parts.push(`${formatNumber(allMinutes)}m`);
  }
  if (seconds > 0) {
    parts.push(`${seconds}s`);
  }

  return parts.join(' ') || '0';
};
